/** The small index of saved spots (IndexedDB). The files themselves live in the Cache API. */

export interface SavedSpot {
  slug: string;
  name: string;
  savedAt: number;
  /** Bytes of everything this spot saved (shared tiles count for each spot that uses them). */
  bytes: number;
  /** Terrain radius that was saved; null when the spot has no lakebed data. */
  radiusM: number | null;
  /** Cache keys (URLs) in the saved cache. */
  keys: string[];
}

export interface SavedStore {
  list(): Promise<SavedSpot[]>;
  put(spot: SavedSpot): Promise<void>;
  delete(slug: string): Promise<void>;
}

const DB = "divespots-offline";
const STORE = "spots";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "slug" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = run(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export const SAVED_CHANGED = "divespots:saved-changed";
const notify = () => window.dispatchEvent(new Event(SAVED_CHANGED));

export const idbStore: SavedStore = {
  list: async () =>
    ((await tx("readonly", (s) => s.getAll())) as SavedSpot[]).sort(
      (a, b) => b.savedAt - a.savedAt,
    ),
  put: async (spot) => {
    await tx("readwrite", (s) => s.put(spot));
    notify();
  },
  delete: async (slug) => {
    await tx("readwrite", (s) => s.delete(slug));
    notify();
  },
};

/** In-memory store for tests. */
export function memoryStore(): SavedStore {
  const m = new Map<string, SavedSpot>();
  return {
    list: async () => [...m.values()],
    put: async (s) => void m.set(s.slug, s),
    delete: async (slug) => void m.delete(slug),
  };
}
